'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { MONO_FONT } from '@/theme';
import { articles } from '@/libs/content/articles';

const stripTags = (s) => (s || '').replace(/<[^>]+>/g, '');

// El primer artículo de articles.js es el destacado.
const a = articles[0];
const FEATURED = {
  topic: a.category,
  readTime: a.readTime.toLowerCase(),
  title: stripTags(a.title),
  excerpt: a.excerpt,
  author: a.author.name,
  role: a.author.role,
  avatar: a.author.avatar || null,
  date: a.date,
  href: `/blog/${a.slug}`,
  image: a.image?.src || null,
  imageAlt: a.image?.alt || '',
};

export default function FeaturedPost() {
  return (
    <Box data-surface="dark"
      component={Link}
      href={FEATURED.href}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
        bgcolor: 'grey.950',
        color: 'common.white',
        borderRadius: 1,
        overflow: 'hidden',
        mb: { xs: 6, md: 9 },
        textDecoration: 'none',
        transition: 'transform 0.18s ease',
        '&:hover': { transform: 'translateY(-2px)' },
        '&:hover .featured-title': { color: '#1ABF9F' },
      }}
    >
      {/* Art panel */}
      <Box
        sx={{
          bgcolor: 'grey.900',
          aspectRatio: { xs: '16/9', md: 'auto' },
          position: 'relative',
          ...(!FEATURED.image && {
            backgroundImage:
              'linear-gradient(135deg, rgba(0,159,139,0.18), transparent 70%), repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 40px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }),
        }}
      >
        {FEATURED.image ? (
          <Box
            component="img"
            src={FEATURED.image}
            alt={FEATURED.imageAlt}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              fontFamily: MONO_FONT,
              fontSize: '11px',
              letterSpacing: '0.12em',
              color: '#1ABF9F',
              textTransform: 'uppercase',
              opacity: 0.7,
            }}
          >
            {FEATURED.topic}
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box
        sx={{
          p: { xs: '32px 24px', md: '48px 44px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {/* Eyebrow */}
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#1ABF9F',
          }}
        >
          <span>{FEATURED.topic}</span>
          <span>{FEATURED.readTime}</span>
        </Box>

        {/* Title */}
        <Typography
          className="featured-title"
          component="h2"
          sx={{
            fontSize: 'clamp(24px, 3vw, 38px)',
            fontWeight: 400,
            letterSpacing: '-0.022em',
            lineHeight: 1.1,
            color: 'common.white',
            transition: 'color 0.15s ease',
          }}
        >
          {FEATURED.title}
        </Typography>

        {/* Excerpt */}
        <Typography
          sx={{
            fontSize: '15px',
            lineHeight: 1.6,
            color: 'text.muted',
            maxWidth: '52ch',
          }}
        >
          {FEATURED.excerpt}
        </Typography>

        {/* Meta footer */}
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            mt: 1,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'grey.800',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.muted',
            flexWrap: 'wrap',
          }}
        >
          {FEATURED.avatar && (
            <Box
              component="img"
              src={FEATURED.avatar}
              alt={FEATURED.author}
              sx={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          )}
          <Box component="span" sx={{ color: 'common.white', fontWeight: 600 }}>
            {FEATURED.author}
          </Box>
          <span>{FEATURED.role}</span>
          <Box component="span" sx={{ color: 'grey.700' }}>·</Box>
          <span>{FEATURED.date}</span>
        </Box>
      </Box>
    </Box>
  );
}
